import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ChatMessage, DevInfo, FollowUpAction } from '../types/protocol';
import { ExecutionProgress } from './ExecutionProgress';

interface MessageBubbleProps {
  message: ChatMessage;
  onConfirm?: () => void;
  onEdit?: (stepId: string) => void;
  onAction?: (action: FollowUpAction) => void;
  devMode?: boolean;
}

function DevInfoPanel({ devInfo }: { devInfo: DevInfo }): React.ReactElement {
  const [expanded, setExpanded] = useState(false);

  const totalTokens = devInfo.usage
    ? devInfo.usage.promptTokens + devInfo.usage.completionTokens
    : null;

  return (
    <div className="sp-dev-info">
      <button
        type="button"
        className="sp-dev-info-toggle"
        onClick={() => setExpanded(!expanded)}
      >
        <span className="sp-dev-info-icon">{expanded ? '▾' : '▸'}</span>
        <span className="sp-dev-info-summary">
          {devInfo.events.length} event{devInfo.events.length !== 1 ? 's' : ''}
          {totalTokens != null && <span className="sp-dev-info-tokens">{totalTokens} tok</span>}
          {devInfo.durationMs != null && <span className="sp-dev-info-duration">{devInfo.durationMs}ms</span>}
        </span>
      </button>
      {expanded && (
        <div className="sp-dev-info-details">
          {devInfo.usage && (
            <div className="sp-dev-info-row">
              <span className="sp-dev-info-label">Tokens</span>
              <span>{devInfo.usage.promptTokens} prompt + {devInfo.usage.completionTokens} completion = {totalTokens}</span>
            </div>
          )}
          {devInfo.durationMs != null && (
            <div className="sp-dev-info-row">
              <span className="sp-dev-info-label">Duration</span>
              <span>{devInfo.durationMs}ms</span>
            </div>
          )}
          {devInfo.events.map((evt, i) => (
            <details key={i} className="sp-dev-info-event">
              <summary>
                <span className="sp-dev-info-event-type">{evt.type}</span>
                <span className="sp-dev-info-event-time">
                  {new Date(evt.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </span>
              </summary>
              <pre className="sp-dev-info-payload">
                {JSON.stringify(evt.payload, null, 2)}
              </pre>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}

export function MessageBubble({ message, onConfirm: _onConfirm, onEdit: _onEdit, onAction, devMode }: MessageBubbleProps): React.ReactElement {
  if (message.role === 'system') {
    return (
      <div className="sp-status-message">
        {message.content}
      </div>
    );
  }

  // Event-style messages (info, workflow started/cancelled) — centered pill
  if (message.subtype === 'event') {
    // Strip markdown formatting for clean pill display
    const cleanText = message.content.replace(/\*\*(.*?)\*\*/g, '$1');
    return (
      <div className="sp-event-message">
        {cleanText}
      </div>
    );
  }

  // Error messages — tinted banner
  if (message.subtype === 'error') {
    return (
      <div className="sp-error-message">
        <div className="sp-markdown">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {message.content}
          </ReactMarkdown>
        </div>
      </div>
    );
  }

  // Success messages — tinted banner
  if (message.subtype === 'success') {
    return (
      <div className="sp-success-message">
        <div className="sp-markdown">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {message.content}
          </ReactMarkdown>
        </div>
        {message.actions && message.actions.length > 0 && (
          <div className="sp-success-actions">
            {message.actions.map((action) => (
              <button
                key={action.id}
                type="button"
                className="sp-success-action"
                title={action.description}
                onClick={() => onAction?.(action)}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  const isAssistant = message.role === 'assistant';

  // Skip rendering recipe preview messages as bubbles — they show in the active field area
  if (message.recipePreview && !message.executionSteps) {
    return <></>;
  }

  return (
    <div className={`sp-bubble sp-bubble-${message.role}`}>
      <div className={`sp-bubble-content${message.streaming ? ' sp-streaming' : ''}`}>
        {message.content && isAssistant ? (
          <div className="sp-markdown">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {message.content}
            </ReactMarkdown>
          </div>
        ) : message.content ? (
          <p className="sp-bubble-text">{message.content}</p>
        ) : null}

        {message.executionSteps && (
          <ExecutionProgress steps={message.executionSteps} />
        )}

        {message.streaming && <span className="sp-cursor" />}
      </div>
      {isAssistant && !message.streaming && (
        <span className="sp-timestamp">
          {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      )}
      {devMode && message.devInfo && <DevInfoPanel devInfo={message.devInfo} />}
    </div>
  );
}
